import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Building2,
  Plus,
  Edit,
  Grid3X3,
  Calendar,
  CalendarCheck,
  MapPin,
  Activity,
  Trash2,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { getMyVenues, deleteVenue } from '../services/venueService';
import { formatCurrency, getStatusBadgeClass } from '../utils/formatters';
import ConfirmModal from '../components/ConfirmModal';
import { useAuth } from '../hooks/useAuth';

export default function VenueList() {
  const { user } = useAuth();
  const isManager = user?.role === 'VenueManager';
  const [venues, setVenues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    loadVenues();
  }, []);

  async function loadVenues() {
    try {
      const data = await getMyVenues();
      setVenues(Array.isArray(data) ? data : Array.isArray(data.venues) ? data.venues : []);
    } catch (err) {
      toast.error('Failed to load venues');
    } finally {
      setLoading(false);
    }
  }

  function promptDelete(venue) {
    setDeleteTarget(venue);
  }

  async function handleDelete() {
    if (!deleteTarget) return;
    const venueId = deleteTarget._id || deleteTarget.id;
    setDeleting(true);
    try {
      await deleteVenue(venueId);
      setVenues((prev) => prev.filter((v) => (v._id || v.id) !== venueId));
      toast.success('Venue deleted successfully');
      setDeleteTarget(null);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete venue');
    } finally {
      setDeleting(false);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-[#264D59] border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#1F2937]">{isManager ? 'Assigned Venues' : 'My Venues'}</h1>
          <p className="mt-1 text-sm text-[#6B7280]">
            {isManager ? 'Manage the venues and courts assigned to you' : 'Manage your FREAC venues and courts'}
          </p>
        </div>
        {!isManager && <button
          onClick={() => navigate('/venues/new')}
          className="btn-primary"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Venue
        </button>}
      </div>

      {venues.length === 0 ? (
        <div className="card p-12 text-center">
          <Building2 className="mx-auto h-16 w-16 text-[#6B7280]" />
          <h3 className="mt-4 text-lg font-medium text-[#1F2937]">No venues yet</h3>
          <p className="mt-2 text-sm text-[#6B7280]">
            {isManager ? 'Ask your venue owner to assign venue access.' : 'Get started by adding your first FREAC venue.'}
          </p>
          {!isManager && <button
            onClick={() => navigate('/venues/new')}
            className="btn-primary mt-6"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Your First Venue
          </button>}
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2 xl:grid-cols-3">
          {venues.map((venue) => (
            <div key={venue._id || venue.id} className="card overflow-hidden">
              <div className="relative h-48 bg-gradient-to-br from-[#E8F4FD] to-[#C5D4DF]">
                {venue.photos && venue.photos.length > 0 ? (
                  <img
                    src={venue.photos[0]}
                    alt={venue.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="flex items-center justify-center h-full">
                    <Building2 className="h-16 w-16 text-[#264D59]" />
                  </div>
                )}
                <div className="absolute top-3 right-3">
                  <span
                    className={`inline-flex items-center px-2.5 py-0.5 rounded-[30px] text-xs font-medium ${getStatusBadgeClass(venue.status || 'active')}`}
                  >
                    {venue.status || 'active'}
                  </span>
                </div>
              </div>

              <div className="p-5">
                <h3 className="text-lg font-semibold text-[#1F2937]">{venue.name}</h3>
                <p className="mt-1 text-sm text-[#6B7280] flex items-center gap-1">
                  <MapPin className="h-3.5 w-3.5" />
                  {venue.city}, {venue.state}
                </p>

                <div className="mt-4 flex items-center gap-4 text-sm text-[#6B7280]">
                  <div className="flex items-center gap-1">
                    <Activity className="h-4 w-4" />
                    <span>{venue.courtCount || venue.courts?.length || 0} courts</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <span aria-hidden="true" className="inline-flex h-4 w-4 items-center justify-center font-semibold">₱</span>
                    <span>{venue.priceRangeLabel || `${formatCurrency(venue.pricePerHour || 0)}/hr`}</span>
                  </div>
                </div>

                <div className="mt-5 flex flex-wrap gap-2">
                  <button
                    onClick={() => navigate(`/venues/${venue._id || venue.id}`)}
                    className="btn-outline text-xs px-3 py-1.5"
                  >
                    <Edit className="h-3.5 w-3.5 mr-1" />
                    Edit
                  </button>
                  <button
                    onClick={() => navigate(`/venues/${venue._id || venue.id}/courts`)}
                    className="btn-outline text-xs px-3 py-1.5"
                  >
                    <Grid3X3 className="h-3.5 w-3.5 mr-1" />
                    Courts
                  </button>
                  <button
                    onClick={() => navigate(`/venues/${venue._id || venue.id}/availability`)}
                    className="btn-outline text-xs px-3 py-1.5"
                  >
                    <Calendar className="h-3.5 w-3.5 mr-1" />
                    Availability
                  </button>
                  <button
                    onClick={() => navigate(`/venues/${venue._id || venue.id}/bookings`)}
                    className="btn-outline text-xs px-3 py-1.5"
                  >
                    <CalendarCheck className="h-3.5 w-3.5 mr-1" />
                    Bookings
                  </button>
                  <button
                    onClick={() => navigate(`/venues/${venue._id || venue.id}/pricing`)}
                    className="btn-outline text-xs px-3 py-1.5"
                  >
                    <span aria-hidden="true" className="mr-1 inline-flex h-3.5 w-3.5 items-center justify-center font-semibold">₱</span>
                    Pricing &amp; promotions
                  </button>
                  {!isManager && <button
                    onClick={() => promptDelete(venue)}
                    className="btn-outline text-xs px-3 py-1.5 text-[#D46C4E] hover:bg-[#FDF1EB] border-[#D46C4E]/30"
                  >
                    <Trash2 className="h-3.5 w-3.5 mr-1" />
                    Delete
                  </button>}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <ConfirmModal
        open={!!deleteTarget}
        title="Delete Venue"
        message={`Are you sure you want to delete "${deleteTarget?.name}"? This will permanently remove the venue and all associated courts, bookings, and data. This cannot be undone.`}
        confirmLabel={deleting ? 'Deleting...' : 'Delete'}
        cancelLabel="Cancel"
        variant="danger"
        onConfirm={handleDelete}
        onCancel={() => { if (!deleting) setDeleteTarget(null); }}
      />
    </div>
  );
}
